import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import { Input } from "@/components/ui/input";
import { MoneyAmountInput } from "@/components/sales/MoneyAmountInput";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  defaultFirstInstallmentDueDate,
  INSTALLMENT_FREQUENCY_OPTIONS,
  INSTALLMENT_MONTH_OPTIONS,
  installmentsMatchBalance,
  sumInstallmentAmounts,
  type InstallmentFrequency,
  type SaleInstallmentDraft,
} from "@/lib/sales/saleInstallments";
import { cn } from "@/lib/utils";
import { ShieldAlert, Wallet } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type Props = {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  hideToggle?: boolean;
  totalSalePrice: number;
  downPayment: number;
  onDownPaymentChange: (amount: number) => void;
  tokenAmount?: number;
  balanceAmount: number; // Financed remaining balance = totalSalePrice - downPayment
  saleDate: string;
  payableDate: string;
  monthCount: number;
  onMonthCountChange: (count: number) => void;
  firstDueDate: string;
  onFirstDueDateChange: (iso: string) => void;
  frequency?: InstallmentFrequency;
  onFrequencyChange?: (frequency: InstallmentFrequency) => void;
  installments: SaleInstallmentDraft[];
  onInstallmentAmountChange: (installmentNo: number, amount: number) => void;
  onInstallmentDueDateChange?: (installmentNo: number, dueDate: string) => void;
  latePenaltyType?: "none" | "percentage" | "fixed";
  onLatePenaltyTypeChange?: (type: "none" | "percentage" | "fixed") => void;
  latePenaltyValue?: string;
  onLatePenaltyValueChange?: (value: string) => void;
  disabled?: boolean;
};

export function SaleInstallmentPlanner({
  enabled,
  onEnabledChange,
  hideToggle = false,
  totalSalePrice,
  downPayment,
  onDownPaymentChange,
  tokenAmount = 0,
  balanceAmount,
  saleDate,
  payableDate,
  monthCount,
  onMonthCountChange,
  firstDueDate,
  onFirstDueDateChange,
  frequency = "monthly",
  onFrequencyChange,
  installments,
  onInstallmentAmountChange,
  onInstallmentDueDateChange,
  latePenaltyType = "none",
  onLatePenaltyTypeChange,
  latePenaltyValue = "",
  onLatePenaltyValueChange,
  disabled = false,
}: Props) {
  const canPlan = totalSalePrice > 0;
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const installmentTotal = useMemo(() => sumInstallmentAmounts(installments), [installments]);
  const totalsMatch = useMemo(
    () => installmentsMatchBalance(installments, balanceAmount, 0.02),
    [installments, balanceAmount],
  );
  const remainingToAllocate = useMemo(
    () => Math.round((balanceAmount - installmentTotal) * 100) / 100,
    [balanceAmount, installmentTotal],
  );

  const [customCountInput, setCustomCountInput] = useState<string>(() =>
    !INSTALLMENT_MONTH_OPTIONS.includes(monthCount as (typeof INSTALLMENT_MONTH_OPTIONS)[number])
      ? String(monthCount)
      : "",
  );

  const isCustomActive = useMemo(() => {
    return (
      customCountInput !== "" ||
      !INSTALLMENT_MONTH_OPTIONS.includes(monthCount as (typeof INSTALLMENT_MONTH_OPTIONS)[number])
    );
  }, [customCountInput, monthCount]);

  useEffect(() => {
    if (INSTALLMENT_MONTH_OPTIONS.includes(monthCount as (typeof INSTALLMENT_MONTH_OPTIONS)[number])) {
      if (customCountInput && parseInt(customCountInput, 10) !== monthCount) {
        setCustomCountInput("");
      }
    } else {
      setCustomCountInput(String(monthCount));
    }
  }, [monthCount]);

  const activeFrequency = useMemo(
    () => INSTALLMENT_FREQUENCY_OPTIONS.find((f) => f.value === frequency) || INSTALLMENT_FREQUENCY_OPTIONS[4],
    [frequency],
  );

  const isTwoColumns = installments.length > 2;
  const col1 = useMemo(() => {
    if (!isTwoColumns) return installments;
    const midpoint = Math.ceil(installments.length / 2);
    return installments.slice(0, midpoint);
  }, [installments, isTwoColumns]);

  const col2 = useMemo(() => {
    if (!isTwoColumns) return [];
    const midpoint = Math.ceil(installments.length / 2);
    return installments.slice(midpoint);
  }, [installments, isTwoColumns]);

  useEffect(() => {
    if (!enabled || !canPlan) return;
    if (!firstDueDate.trim()) {
      onFirstDueDateChange(defaultFirstInstallmentDueDate(saleDate, payableDate, frequency));
    }
  }, [enabled, canPlan, firstDueDate, saleDate, payableDate, frequency, onFirstDueDateChange]);

  return (
    <div className="space-y-3 rounded-xl border border-border/60 bg-muted/10 p-3.5 sm:p-4 shadow-2xs">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 border-b border-border/40 pb-2.5">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-primary shrink-0" />
          <h4 className="text-sm font-bold text-foreground">Installment Plan & Schedule</h4>
          <span className="hidden sm:inline-block text-[11px] text-muted-foreground">
            — Down payment, frequency, and schedule breakdown
          </span>
        </div>
        {!hideToggle ? (
          <Switch
            checked={enabled}
            onCheckedChange={onEnabledChange}
            disabled={disabled || !canPlan}
            aria-label="Enable installment plan"
          />
        ) : null}
      </div>

      {!canPlan ? (
        <p className="text-xs text-muted-foreground">Enter a sale price greater than zero to plan installments.</p>
      ) : null}

      {enabled && canPlan ? (
        <>
          {/* 3 Overview Cards: Sale Price, Down Payment Input, Financed Balance (Strictly 2 lines) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            <div className="rounded-lg border border-border/60 bg-background/80 px-3 py-2 space-y-1">
              <div className="flex items-center justify-between min-h-[1.25rem]">
                <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                  Total Sale Price
                </span>
                {pkrAmountShortLabel(totalSalePrice) && (
                  <span className="text-[10px] font-semibold text-muted-foreground">
                    {pkrAmountShortLabel(totalSalePrice)}
                  </span>
                )}
              </div>
              <div className="h-8 flex items-center justify-end">
                <p className="text-sm sm:text-base font-bold font-display text-foreground leading-none text-right">
                  {formatCurrency(totalSalePrice)}
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 space-y-1">
              <div className="flex items-center justify-between min-h-[1.25rem] gap-1">
                <span className="text-[9px] font-bold uppercase tracking-wider text-primary truncate">
                  Down Payment (Upfront)
                </span>
                {tokenAmount > 0 ? (
                  <span className="text-[10px] font-semibold text-primary/90 shrink-0 tabular-nums">
                    Token: {pkrAmountShortLabel(tokenAmount) || formatCurrency(tokenAmount)} · Due: {pkrAmountShortLabel(Math.max(0, downPayment - tokenAmount)) || formatCurrency(Math.max(0, downPayment - tokenAmount))}
                  </span>
                ) : pkrAmountShortLabel(downPayment) ? (
                  <span className="text-[10px] font-semibold text-primary/90 shrink-0">
                    {pkrAmountShortLabel(downPayment)}
                  </span>
                ) : null}
              </div>
              <div className="h-8 flex items-center">
                <MoneyAmountInput
                  value={downPayment}
                  size="sm"
                  showShortLabel={false}
                  disabled={disabled}
                  placeholder="0.00"
                  inputClassName="glass-input h-8 text-xs sm:text-sm font-bold tabular-nums w-full text-right"
                  onChange={(amount) => onDownPaymentChange(Math.min(totalSalePrice, Math.max(0, amount)))}
                />
              </div>
            </div>

            <div className="rounded-lg border border-purple-500/30 bg-purple-500/5 px-3 py-2 space-y-1">
              <div className="flex items-center justify-between min-h-[1.25rem]">
                <span className="text-[9px] font-bold uppercase tracking-wider text-purple-700 dark:text-purple-300">
                  Financed Remaining Balance
                </span>
                {pkrAmountShortLabel(balanceAmount) ? (
                  <span className="text-[10px] font-semibold text-purple-700/80 dark:text-purple-300/80">
                    {pkrAmountShortLabel(balanceAmount)}
                  </span>
                ) : null}
              </div>
              <div className="h-8 flex items-center justify-end">
                <p className="text-sm sm:text-base font-bold font-display text-purple-700 dark:text-purple-300 tabular-nums leading-none text-right">
                  {formatCurrency(balanceAmount)}
                </p>
              </div>
            </div>
          </div>

          {/* Schedule Controls: Frequency, First Due Date, Number of Installments */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-2.5 items-end pt-0.5">
            <div className="space-y-1 sm:col-span-1 lg:col-span-3">
              <Label className="text-[11px] font-semibold text-muted-foreground">
                Frequency
              </Label>
              <Select
                value={frequency}
                onValueChange={(val) => onFrequencyChange?.(val as InstallmentFrequency)}
                disabled={disabled}
              >
                <SelectTrigger className="glass-input h-8 text-xs">
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent position="popper">
                  {INSTALLMENT_FREQUENCY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1 sm:col-span-1 lg:col-span-3">
              <Label className="text-[11px] font-semibold text-muted-foreground">
                First Due Date
              </Label>
              <DatePartsPicker
                value={firstDueDate}
                onChange={onFirstDueDateChange}
                fromYear={currentYear}
                toYear={currentYear + 15}
                placeholder="Select first due date"
                disabled={disabled}
                className="h-8 text-xs font-semibold"
              />
            </div>

            <div className="space-y-1 sm:col-span-2 lg:col-span-6">
              <div className="flex items-center justify-between">
                <Label className="text-[11px] font-semibold text-muted-foreground">
                  Number of Installments
                </Label>
                <span className="text-[10px] text-muted-foreground font-medium">
                  {monthCount} {activeFrequency.label.toLowerCase()}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {INSTALLMENT_MONTH_OPTIONS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      setCustomCountInput("");
                      onMonthCountChange(n);
                    }}
                    className={cn(
                      "h-8 min-w-[2.25rem] rounded-md border px-2.5 text-xs font-bold transition-all shadow-2xs",
                      !isCustomActive && monthCount === n
                        ? "border-primary bg-primary text-primary-foreground shadow-xs"
                        : "border-border/60 bg-card text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                    )}
                  >
                    {n}
                  </button>
                ))}
                <div
                  className={cn(
                    "flex items-center gap-1 px-2 py-0.5 rounded-md border transition-all h-8",
                    isCustomActive
                      ? "border-primary/60 bg-primary/10 ring-1 ring-primary/40 shadow-2xs"
                      : "border-border/60 bg-muted/20",
                  )}
                >
                  <span className="text-[11px] text-muted-foreground font-medium">Custom:</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    disabled={disabled}
                    value={customCountInput}
                    placeholder="1–120"
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, "");
                      setCustomCountInput(digits);
                      const v = parseInt(digits, 10);
                      if (!Number.isNaN(v) && v >= 1) {
                        onMonthCountChange(Math.min(120, v));
                      }
                    }}
                    onBlur={() => {
                      const v = parseInt(customCountInput, 10);
                      if (Number.isNaN(v) || v < 1) {
                        setCustomCountInput(
                          INSTALLMENT_MONTH_OPTIONS.includes(monthCount as (typeof INSTALLMENT_MONTH_OPTIONS)[number])
                            ? ""
                            : String(monthCount),
                        );
                      } else {
                        const clamped = Math.min(120, Math.max(1, v));
                        setCustomCountInput(String(clamped));
                        onMonthCountChange(clamped);
                      }
                    }}
                    className="glass-input h-6 w-14 px-1 text-xs font-bold tabular-nums rounded text-center bg-background"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Compact Late Payment Penalty Row */}
          {onLatePenaltyTypeChange ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/50 bg-background/50 px-3 py-1.5">
              <div className="flex items-center gap-1.5">
                <ShieldAlert className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
                <span className="text-xs font-semibold text-foreground">Late Fine / Penalty:</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  value={latePenaltyType}
                  onValueChange={(v) =>
                    onLatePenaltyTypeChange(v as "none" | "percentage" | "fixed")
                  }
                  disabled={disabled}
                >
                  <SelectTrigger className="glass-input h-7.5 text-xs w-40">
                    <SelectValue placeholder="Select penalty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" className="text-xs">No penalty</SelectItem>
                    <SelectItem value="percentage" className="text-xs">Percentage (%) overdue</SelectItem>
                    <SelectItem value="fixed" className="text-xs">Fixed fine (PKR)</SelectItem>
                  </SelectContent>
                </Select>
                {latePenaltyType !== "none" ? (
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min={0}
                      max={latePenaltyType === "percentage" ? 100 : undefined}
                      step="any"
                      disabled={disabled}
                      className="glass-input h-7.5 w-24 text-xs font-semibold tabular-nums"
                      value={latePenaltyValue}
                      onChange={(e) => onLatePenaltyValueChange?.(e.target.value)}
                      placeholder={latePenaltyType === "percentage" ? "%" : "PKR"}
                    />
                    <span className="text-[10px] font-medium text-muted-foreground">
                      {latePenaltyType === "percentage" ? "%" : "PKR"}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Compact Monthly Installment Schedule Table */}
          {installments.length > 0 ? (
            <div className="space-y-1.5 pt-0.5">
              <div className="flex items-center justify-between px-0.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-foreground">
                    Installment Schedule
                  </span>
                  <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                    {installments.length} {activeFrequency.label.toLowerCase()}
                  </span>
                </div>
                <div>
                  {totalsMatch ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      Balanced
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                      {remainingToAllocate > 0
                        ? `Remaining: ${formatCurrency(remainingToAllocate)}`
                        : `Over: ${formatCurrency(Math.abs(remainingToAllocate))}`}
                    </span>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-border/60 bg-card shadow-2xs overflow-hidden">
                <div className="max-h-150 overflow-y-auto">
                  <div
                    className={cn(
                      "grid",
                      isTwoColumns
                        ? "grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border/30"
                        : "grid-cols-1",
                    )}
                  >
                    {[col1, ...(isTwoColumns && col2.length > 0 ? [col2] : [])].map((items, colIdx) => (
                      <div key={colIdx} className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="sticky top-0 bg-muted/95 backdrop-blur-xs z-10 border-b border-border/40">
                            <tr className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                              <th className="w-8 px-2 py-1.5 text-center">#</th>
                              <th className="px-2 py-1.5 text-left">Due Date</th>
                              <th className="px-2 py-1.5 text-right">Amount</th>
                              <th className="w-16 px-2 py-1.5 text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border/20">
                            {items.map((row) => (
                              <tr key={row.installment_no} className="hover:bg-muted/30 transition-colors">
                                <td className="w-8 px-2 py-1 text-center font-bold tabular-nums text-muted-foreground text-xs">
                                  {row.installment_no}
                                </td>
                                <td className="px-2 py-1 text-left">
                                  <div className="w-full min-w-[7.5rem] max-w-[12rem]">
                                    <DatePartsPicker
                                      value={row.due_date}
                                      onChange={(iso) =>
                                        onInstallmentDueDateChange?.(row.installment_no, iso)
                                      }
                                      fromYear={currentYear}
                                      toYear={currentYear + 15}
                                      displayFormat="MMM d, yyyy"
                                      placeholder="Select due date"
                                      disabled={disabled}
                                      className="h-7 text-xs font-semibold px-2 bg-background border-border/60 shadow-none w-full"
                                    />
                                  </div>
                                </td>
                                <td className="px-2 py-1 text-right">
                                  <div className="flex items-center justify-end">
                                    <MoneyAmountInput
                                      value={row.amount}
                                      size="sm"
                                      showShortLabel={false}
                                      disabled={disabled}
                                      inputClassName="w-24 sm:w-28 h-7 text-xs font-bold tabular-nums px-2 bg-background border-border/60 shadow-none text-right"
                                      onChange={(amount) =>
                                        onInstallmentAmountChange(row.installment_no, amount)
                                      }
                                    />
                                  </div>
                                </td>
                                <td className="w-16 px-2 py-1 text-center">
                                  <span className="inline-block px-1.5 py-0.5 text-[9px] font-semibold rounded bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20">
                                    Pending
                                  </span>
                                </td>
                              </tr>
                            ))}
                            {/* Empty spacer row for visual height balance if odd count */}
                            {isTwoColumns && items.length < col1.length
                              ? Array.from({ length: col1.length - items.length }).map((_, idx) => (
                                  <tr key={`spacer-${idx}`} className="h-[37px] opacity-0 select-none pointer-events-none">
                                    <td colSpan={4}>&nbsp;</td>
                                  </tr>
                                ))
                              : null}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sticky Footer for Totals */}
                <div className="border-t border-border/40 bg-muted/40 px-3 py-1.5 flex flex-wrap items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground font-medium">Scheduled:</span>
                    <span className="font-bold font-display text-foreground text-sm tabular-nums">
                      {formatCurrency(installmentTotal)}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      ({installments.length})
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-[11px]">Financed:</span>
                    <span className="font-bold text-xs text-foreground tabular-nums">
                      {formatCurrency(balanceAmount)}
                    </span>
                    <span
                      className={cn(
                        "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide",
                        totalsMatch
                          ? "border border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                          : "border border-destructive/30 bg-destructive/15 text-destructive",
                      )}
                    >
                      {totalsMatch ? "Matched" : "Mismatch"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
